/**
 * Events API Service
 * Handles event-related API calls
 */

import { ApiClient } from '../core/api-client';
import { isApiError } from '../core/errors';
import { logger } from '../../utils/logger';

export interface Event {
  id: string;
  title: string;
  description?: string;
  date: string;
  startTime?: string;
  endTime?: string;
  location?: string;
  category?: string;
  status?: 'draft' | 'published' | 'archived';
  image?: string;
  registrationRequired?: boolean;
  attendees?: number;
  capacity?: number;
}

export interface EventsResponse {
  data: Event[];
  pagination?: {
    total: number;
    limit: number;
    offset: number;
  };
}

export interface EventResponse {
  data: Event;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export class EventService {
  private client: ApiClient;

  constructor() {
    this.client = ApiClient.getInstance(API_BASE_URL);
  }

  /**
   * Get all events with filtering
   */
  async getEvents(options?: {
    limit?: number;
    offset?: number;
    status?: string;
    category?: string;
    congregationId?: string;
  }): Promise<{ events: Event[]; total: number }> {
    try {
      const params = new URLSearchParams();
      if (options?.limit) params.append('limit', options.limit.toString());
      if (options?.offset) params.append('offset', options.offset.toString());
      if (options?.status) params.append('status', options.status);
      if (options?.category) params.append('category', options.category);
      if (options?.congregationId) params.append('congregationId', options.congregationId);

      const url = `/public/event${params.toString() ? '?' + params.toString() : ''}`;
      const response = await this.client.get<Event[]>(url, {
        credentials: 'include',
      });

      // API returns array directly, not wrapped in data object
      const events = Array.isArray(response.data) ? response.data : [];
      
      return {
        events,
        total: events.length,
      };
    } catch (error) {
      if (isApiError(error) && (error.statusCode === 401 || error.statusCode === 403)) {
        // Gracefully handle auth errors
        return { events: [], total: 0 };
      }
      console.debug('[EventService] Failed to fetch events', error);
      return { events: [], total: 0 };
    }
  }

  /**
   * Get upcoming events
   */
  async getUpcomingEvents(limit?: number): Promise<Event[]> {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());

      const url = `/event/upcoming${params.toString() ? '?' + params.toString() : ''}`;
      const response = await this.client.get<EventsResponse>(url, {
        credentials: 'include',
      });

      return response.data?.data || [];
    } catch (error) {
      logger.error('Failed to fetch upcoming events', error);
      return [];
    }
  }

  /**
   * Get event by ID
   */
  async getEventById(eventId: string): Promise<Event | null> {
    try {
      const response = await this.client.get<EventResponse>(`/public/event/${eventId}`, {
        credentials: 'include',
      });

      return response.data?.data || null;
    } catch (error) {
      if (isApiError(error) && error.statusCode === 404) {
        logger.warn(`Event ${eventId} not found`);
        return null;
      }

      logger.error('Failed to fetch event', error);
      return null;
    }
  }

  /**
   * Create event
   */
  async createEvent(data: Partial<Event>): Promise<Event> {
    try {
      const response = await this.client.post<EventResponse>('/event', data, {
        credentials: 'include',
      });

      if (!response.data?.data) {
        throw new Error('Invalid response from server');
      }

      return response.data.data;
    } catch (error) {
      logger.error('Failed to create event', error);
      throw error;
    }
  }

  /**
   * Update event
   */
  async updateEvent(eventId: string, data: Partial<Event>): Promise<Event> {
    try {
      const response = await this.client.put<EventResponse>(`/event/${eventId}`, data, {
        credentials: 'include',
      });

      if (!response.data?.data) {
        throw new Error('Invalid response from server');
      }

      return response.data.data;
    } catch (error) {
      logger.error('Failed to update event', error);
      throw error;
    }
  }

  /**
   * Delete event
   */
  async deleteEvent(eventId: string): Promise<void> {
    try {
      await this.client.delete(`/event/${eventId}`, {
        credentials: 'include',
      });
    } catch (error) {
      logger.error('Failed to delete event', error);
      throw error;
    }
  }

  /**
   * Register user for event
   */
  async registerForEvent(eventId: string): Promise<void> {
    try {
      await this.client.post(`/event/${eventId}/register`, {}, { credentials: 'include' });
    } catch (error) {
      logger.error('Failed to register for event', error);
      throw error;
    }
  }

  /**
   * Unregister user from event
   */
  async unregisterFromEvent(eventId: string): Promise<void> {
    try {
      await this.client.post(`/event/${eventId}/unregister`, {}, { credentials: 'include' });
    } catch (error) {
      logger.error('Failed to unregister from event', error);
      throw error;
    }
  }
}

// Export singleton instance
export const eventService = new EventService();
